import { DateFilterTypesEnum, PaymentService } from "@propertelligent/client-api";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import type React from "react";
import apiHelper from "../../../utils/apiHelper";
import DateHelper from "../../../utils/DateHelper";
import SingleDropdown from "../../singleDropdown/SingleDropdown";
import Chevren_downIcon from "../../../assets/svgs/Chevren_down";

function DocIcon() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export type PaymentItem = {
  id: any;
  fromPaymentMethodId: any;
  title: string;
  dueDate: string;
  total: number;
};

export type PaymentState = {
  checked: boolean;
  portion: "full" | "partial" | null;
  amount: number;
};

export type PaymentsListPanelProps = {
  payments?: PaymentItem[];
  payeeAccountId?: number;
  onPaymentChange: (states: PaymentState[]) => void;
  setLoadedPayments: (payments: PaymentItem[]) => void;
};

export default function PaymentsListPanel({
  payments = [],
  onPaymentChange,
  payeeAccountId,
  setLoadedPayments,
}: PaymentsListPanelProps) {
  const [paymentStates, setPaymentStates] = useState<PaymentState[]>([]);
  const [loadedPayments, setLocalLoadedPayments] = useState<PaymentItem[]>([]);
  const [dateFilterType, setDateFilterType] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const allRef = useRef<HTMLInputElement>(null);

  const allChecked = paymentStates.every((s) => s.checked);
  const someChecked = paymentStates.some((s) => s.checked);

  // ✅ Helper to format currency
  const fmt = (n: number) => `$${n.toFixed(2)}`;

  // ✅ Unified Payment Mapping
  const mapPayments = (items: any[]): PaymentItem[] =>
    items.map((item) => ({
      id: item.id.toString(),
      fromPaymentMethodId: item.fromPaymentMethodId,
      title: item.payerPayeeName || item.description || "Untitled Payment",
      dueDate: item.formatted?.DueDateDateOnly ?? item.dueDate,
      total: item.amount,
    }));

  // ✅ Fetch Payments (unified logic)
const fetchPayeeList = useCallback(async () => {
  if (!payeeAccountId) return;
  setLoading(true);

  try {
    const clientDate = DateHelper.getClientDate();
    let response;

    if (dateFilterType === null) {
      response = await apiHelper(
        PaymentService.getApiPaymentDueAccountPayeelist,
        { showNotification: false },
        payeeAccountId
      );
    } else {
      response = await apiHelper(
        PaymentService.getApiPaymentDueAccountPayeelist,
        { showNotification: false },
        payeeAccountId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        dateFilterType,
        clientDate,
        undefined,
        undefined,
        "DueDate"
      );
    }

    // ✅ FIX: your data is inside response.data.success (not .items)
    const paymentsArray =
      Array.isArray(response?.data?.items)
        ? response.data.items
        : Array.isArray(response?.data?.success)
        ? response.data.success
        : [];

    if (paymentsArray.length > 0) {
      const mapped = paymentsArray.map((item) => ({
        id: item.id.toString(),
        title: item.payerPayeeName || item.description || "Untitled Payment",
        dueDate: item.formatted?.DueDateDateOnly ?? item.dueDate,
        total: item.amount,
      }));

      setLocalLoadedPayments(mapped);
      setLoadedPayments(mapped);
    } else {
      console.warn("⚠️ No payments found in API response");
    }
  } catch (err) {
    console.error("Error fetching payments:", err);
  } finally {
    setLoading(false);
  }
}, [payeeAccountId, dateFilterType, setLoadedPayments]);




  // ✅ Initialize states when payments update
  useEffect(() => {
    setPaymentStates(
      loadedPayments.map((p, i) => ({
        checked: i === 0,
        portion: i === 0 ? "full" : null,
        amount: i === 0 ? p.total / 2 : 0,
      }))
    );
  }, [loadedPayments]);

  // ✅ Sync parent
  useEffect(() => {
    if (allRef.current) {
      allRef.current.indeterminate = !allChecked && someChecked;
    }
    onPaymentChange(paymentStates);
  }, [paymentStates, allChecked, someChecked, onPaymentChange]);

  // ✅ Fetch when filter or payee changes
  useEffect(() => {
    fetchPayeeList();
  }, [fetchPayeeList]);

  // ✅ Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setPaymentStates((prev) =>
      prev.map((s) => ({ ...s, checked, portion: checked ? s.portion ?? "full" : null }))
    );
  };

  const handleCheckboxChange = (index: number, checked: boolean) => {
    setPaymentStates((prev) =>
      prev.map((s, i) =>
        i === index ? { ...s, checked, portion: checked ? s.portion ?? "full" : null } : s
      )
    );
  };

  const handlePortionChange = (index: number, portion: "full" | "partial") => {
    setPaymentStates((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, checked: true, portion, amount: loadedPayments[i].total }
          : s
      )
    );
  };

  const handleAmountChange = (index: number, value: number) => {
    const max = loadedPayments[index].total;
    const clamped = Math.max(0, Math.min(max, Number.isFinite(value) ? value : 0));
    setPaymentStates((prev) =>
      prev.map((s, i) => (i === index ? { ...s, amount: clamped } : s))
    );
  };

  // ✅ Dropdown filter menu
  const monthFilter = useMemo(
    () => ({
      NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
      NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
      NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
    }),
    []
  );

  const MENU_ITEMS = [
    { label: "All Payments", action: () => setDateFilterType(null) },
    { isDivider: true },
    { label: "Next 30 Days", action: () => setDateFilterType(monthFilter.NEXT_30_DAYS) },
    { isDivider: true },
    { label: "Next 3 months", action: () => setDateFilterType(monthFilter.NEXT_3_MONTHS) },
    { isDivider: true },
    { label: "Next 6 months", action: () => setDateFilterType(monthFilter.NEXT_6_MONTHS) },
  ];

  return (
    <section className="panel left-panel" aria-labelledby="payments-heading">
      <header className="panel-header">
        <h2 id="payments-heading" className="panel-title">
          {loadedPayments.length} Payments
        </h2>
        <SingleDropdown
          triggerText="All Payments"
          triggerIcon={<Chevren_downIcon width="18" height="18" />}
          triggerIconPosition="right"
          menuItems={MENU_ITEMS}
        />
      </header>

      <div className="bulk-row">
        <label className="checkbox">
          <input type="checkbox" ref={allRef} checked={allChecked} onChange={handleSelectAll} />
          <span className="checkbox-label">Select All</span>
        </label>
      </div>

      <ul className="payments-list">
        {loadedPayments.map((p, i) => {
          const state = paymentStates[i];
          if (!state) return null;

          const isPartial = state.portion === "partial";
          const outstanding = p.total - (state.amount || 0);

          return (
            <li key={p.id} className={`panel-payment-row ${state.checked ? "active" : ""}`}>
              <div className="row-left">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={state.checked}
                    onChange={(e) => handleCheckboxChange(i, e.target.checked)}
                  />
                </label>
                <div className="row-content">
                  <div className="row-title">{p.title}</div>
                  <div className="row-sub">
                    <span className="sub-item">
                      <DocIcon />
                      <span>Payment of electricity refer...</span>
                    </span>
                    <a className="view-more" href="#" onClick={(e) => e.preventDefault()}>
                      View More
                    </a>
                  </div>
                  <div className="row-sub">
                    <span className="sub-item">
                      <CalendarIcon />
                      <span>Due on: {p.dueDate}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className={`row-right ${state.portion ?? ""}`}>
                <fieldset className="radio-group">
                  <label className="radio">
                    <input
                      type="radio"
                      name={`portion-${p.id}`}
                      disabled={!state.checked}
                      checked={state.portion === "full"}
                      onChange={() => handlePortionChange(i, "full")}
                    />
                    <span>Full</span>
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      name={`portion-${p.id}`}
                      disabled={!state.checked}
                      checked={isPartial}
                      onChange={() => handlePortionChange(i, "partial")}
                    />
                    <span>Partial</span>
                  </label>
                </fieldset>

                {state.checked && isPartial && (
                  <div className="currency-input">
                    <span className="prefix">$</span>
                    <input
                      type="number"
                      min={0}
                      max={p.total}
                      value={state.amount}
                      onChange={(e) => handleAmountChange(i, parseFloat(e.target.value))}
                    />
                  </div>
                )}
                <div className="amount">{fmt(p.total)}</div>
              </div>

              {state.checked && isPartial && (
                <div className="row-bottom">
                  <div className="outstanding-bar">
                    <div className="outstanding-label">Outstanding Amount</div>
                    <div className="outstanding-amount">{fmt(outstanding)}</div>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}